import { ArrowLeft } from "@phosphor-icons/react";
import { getTaskData, TaskIdentifier } from "../../data/Tasks/Task";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CanvasViewport } from "./CanvasViewport";
import { useFirebaseImage } from "../../providers/FirebaseImageProvider";
import { CanvasViewportProvider } from "./CanvasViewportContext";
import { Sidebar } from "./Sidebar";
import { Toolbar } from "./Toolbar";

export type CowboyEditorProps = {
  projectId: string;
  taskIdentifier: TaskIdentifier;
  imageId: string;
}

export function CowboyEditor({ projectId, taskIdentifier, imageId }: CowboyEditorProps) {
  // MARK: UI References

  const viewportContainerRef = useRef<HTMLDivElement>(null);

  // MARK: Image loading

  const { getImageAsBitmap } = useFirebaseImage();

  /**
   * The content of the image to be displayed on the canvas.
   */
  const [image, setImage] = useState<ImageBitmap | null>(null);

  useEffect(() => {
    getImageAsBitmap(imageId)
      .then((image) => {
        console.log("Image content loaded.");

        setImage(image);
      })
      .catch((error) => {
        console.error("Error loading image content:", error);
      });
  }, [imageId, getImageAsBitmap]);

  const taskData = useMemo(() => {
    return getTaskData(taskIdentifier);
  }, [taskIdentifier]);

  // MARK: Navigation

  const navigate = useNavigate();

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // MARK: UI Definition

  const pageWidth = useMemo(() => {
    return window.innerWidth;
  }, []);

  const pageHeight = useMemo(() => {
    return window.innerHeight;
  }, []);

  return (
    <div className="flex-1 flex items-center justify-center fixed inset-0 overflow-hidden">
      <div className="absolute top-4 left-4 transform bg-white bg-opacity-30 backdrop-filter backdrop-blur-lg p-2 rounded-full shadow-md z-10 flex items-center">
        <button
          className="mx-2 p-2 bg-gradient-to-r from-indigo-400 to-indigo-600 text-white rounded-full shadow-lg hover:from-indigo-500 hover:to-indigo-700 transition duration-300"
          onClick={() => goBack()}
        >
          <ArrowLeft weight='duotone' size={24} />
        </button>
      </div>

      <Toolbar />

      <div className="absolute w-full h-full">
        {image ? <CanvasViewport image={image} width={pageWidth} height={pageHeight} /> : <div>Loading...</div>}

        <Sidebar taskData={taskData} height={720} />
      </div>
    </div>
  );
}

// MARK: Helpers